import React, { useState, useEffect } from 'react';
import { openCanvasPortal } from '@/utils/linksUtils';
import { useTasklistDisplayRuntime } from '@/hooks/useTasklistDisplayRuntime/useTasklistDisplayRuntime';
import { useSchool } from '@context/SchoolContext';
import TaskCard from '@/components/TaskCard/TaskCard';
import { LogoSkeleton } from '@components/Skeletons/Skeletons'
import k12Logo from '@assets/images/k12_logo.svg';
import calendar from '@assets/icons/calendar.svg';
import handWave from '@assets/icons/hand-wave.png';
import Button from '@/components/Button/Button';
import { RedirectIcon } from '@/components/MenusIcons/MenusIcons';

type Role = 'LEARNING_COACH' | 'STUDENT';

export interface HeroProps {
  role: Role;
  loggedInUserName: string;
  hasMultipleStudents?: boolean;
  accountId?: number;
  identityId: string;
  firstName: string;
  lastName: string;
  nickName?: string | null;
  gradeId: string;
  age?: number;
  schoolId: string;
  schoolName: string;
  schoolShortName: string;
  entryDate?: string | null;
  schoolLogo: string;
  heroImage: string;
  onSwitchStudent?: () => void;
  /** Teacher preview: show when the logged-in teacher has more than one school. */
  hasMultipleSchools?: boolean;
  onSwitchSchools?: () => void;
  tasks: {
    id: number;
    title: string;
    completed: boolean;
    checklistName: string;
  }[];
  readOnly?: boolean;
  isTeacherView?: boolean;
  /** When false, hides the incomplete-task count badge on the task card header. */
  showTaskBadge?: boolean;
}

const Hero: React.FC<HeroProps> = ({
  role,
  loggedInUserName,
  hasMultipleStudents = false,
  firstName,
  lastName,
  nickName,
  gradeId,
  schoolShortName,
  schoolName,
  accountId,
  identityId,
  age,
  schoolId,
  entryDate,
  schoolLogo,
  heroImage,
  onSwitchStudent,
  hasMultipleSchools = false,
  onSwitchSchools,
  tasks,
  showTaskBadge = true,
  isTeacherView = false,
}) => {
  const displayName = nickName || firstName;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const parsedStartDate = entryDate
    ? new Date(
        Number(entryDate.split('-')[0]),
        Number(entryDate.split('-')[1]) - 1,
        Number(entryDate.split('-')[2])
      )
    : null; 
  const daysUntilStart = parsedStartDate
  ? Math.ceil((parsedStartDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  : null;
  
  // logo    
  const { data: schoolData } = useSchool();
  const logoSrc = schoolData?.logo_emblem_uri || '';
  
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
    
  useEffect(() => {
      if (!logoSrc || logoSrc.trim() === '') {
        const timer = setTimeout(() => {
          setShowLogo(true);
        }, 7000);
        return () => clearTimeout(timer);
      } else {
        setShowLogo(true);
      }
    }, [logoSrc]);
  
  const { runtime, k12SchoolHref, showK12SchoolCta, isReady: k12PortalReady } = useTasklistDisplayRuntime();
  const lpOn = runtime.launchpadAvailable;
  const olsOn = runtime.olsAvailable;

  /** Homepage K12 module copy — matches LP/OLS matrix (prototype sandbox). */
  const k12SchoolModuleDescription = (): string | null => {
    if (!k12PortalReady) return null;
    if (!lpOn && !olsOn) {
      return "You'll be able to log in to the K12 School (OLS) on your first day of school.";
    }
    if (lpOn && !olsOn) {
      return 'Before school starts, explore K12 Launch Pad, your front door to the K12 School (OLS).';
    }
    return null;
  };

  const handleOLSClick = () => {
    openCanvasPortal(k12SchoolHref);
  };

  const renderOLSAccessSection = () => {
    const description = k12SchoolModuleDescription();
    const showCta = k12PortalReady && showK12SchoolCta;

    return (
      <div className="countdown-desc k12-school-hero-module">
        {description ? (
          <p className="ols-info-text ols-module-description">{description}</p>
        ) : null}
        {showCta ? (
          <Button
            label="Go to K12 School (OLS)"
            icon={<RedirectIcon />}
            iconPosition="after"
            size="lg"
            variant="fill"
            width="fill"
            color="primary"
            breakpoint="all"
            onClick={handleOLSClick}
            className="ols_btn"
            disabled={!k12SchoolHref}
          />
        ) : null}
      </div>
    );
  };

  return (
    <div className="hero-container">
      <div className="welcome">
            <div className='welcome-content'>
                <h1 className='hero-title'><span>Hello, {loggedInUserName}</span> 
                  <img src={handWave} className="hand-wave" />
                </h1>
                <p className='hero-desc'>
                    We're glad you're here! Explore this site before school starts to get helpful start-up tips. On your first day of school, you'll be able to log in to the K12 School (OLS), where you'll find your courses and schedule.
                </p>
            </div>
            <div className='hero-image'>
                <img src={heroImage} alt="hero" className="hero-image" />
            </div>
        </div>

      <div className="student-content">
        <div className="student-card">
            <div className="student-details-wrapper">
                <div className="student-logo">
                  {!logoLoaded && <LogoSkeleton />}
                    {showLogo && (
                      <img 
                        src={logoSrc || k12Logo}
                        alt={`${schoolData?.abbreviation || 'K12'} Logo`}
                        className="logo"
                        style={{ display: logoLoaded ? 'block' : 'none' }}
                        onLoad={() => setLogoLoaded(true)}
                        onError={() => setLogoLoaded(true)}
                      />
                    )}
                </div>
                <div className="student-info">
                    <h3>{displayName}</h3>
                    <p>
                      {schoolShortName} | Grade{gradeId ? ` ${gradeId}` : ''}
                    </p>
                    {!isTeacherView && (
                      <p>Student ID:{identityId ? ` ${identityId}` : ''}</p>
                    )}
                </div>
            {hasMultipleStudents && !isTeacherView && (
                <div className="switch-student">
                    <button className="switch" type="button" onClick={onSwitchStudent}>
                      Switch Students
                    </button>
                </div>
            )}
            {isTeacherView && hasMultipleSchools && (
                <div className="switch-student">
                    <button className="switch" type="button" onClick={onSwitchSchools}>
                      Switch Schools
                    </button>
                </div>
            )}
            </div>
            <div className="countdown">
              {parsedStartDate ? (
                daysUntilStart !== null && daysUntilStart > 0 ? (
                  <>
                    <span className='eyebrow'>FIRST DAY COUNTDOWN</span>
                    <h2 className='countdown-title'>
                      <img src={calendar} alt="Calendar" className="calendar-icon" /> {daysUntilStart} days away!
                    </h2>
                    <p className='countdown-desc'>
                      {role === 'LEARNING_COACH'
                        ? `${displayName} starts school on `
                        : `You start school on `}
                      <span>
                        {entryDate && (() => {
                          const [year, month, day] = entryDate.split('-').map(Number);
                          const date = new Date(year, month - 1, day);
                          const dayWithSuffix = (d => (d > 3 && d < 21) ? 'th' : ['th', 'st', 'nd', 'rd'][d % 10] || 'th')(day);
                          return `${date.toLocaleString('default', { month: 'long' })} ${day}${dayWithSuffix}, ${year}`;
                        })()}
                      </span>
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className='countdown-title'>
                      <img src={calendar} alt="Calendar" className="calendar-icon" /> School has started!
                    </h2>
                    {renderOLSAccessSection()}
                  </>
                )
              ) : (
                <>
                  <h2 className='countdown-title'>Welcome to {schoolShortName}</h2>
                  {renderOLSAccessSection()}
                </>
              )}
            </div>
        </div>
        <TaskCard
          tasks={tasks}
          role={role}
          displayName={displayName}
          showTaskBadge={showTaskBadge}
        />
      </div>
    </div>
  );
};

export default Hero;
